import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  Smartphone, 
  Cloud, 
  Brain, 
  Server, 
  Shield, 
  Building2, 
  Compass 
} from 'lucide-react';

const WhatWeDoSection = () => {
  const { t } = useLanguage();

  const services = [
    { key: 'mobile', icon: Smartphone },
    { key: 'cloud', icon: Cloud },
    { key: 'ai', icon: Brain },
    { key: 'backend', icon: Server },
    { key: 'security', icon: Shield },
    { key: 'financial', icon: Building2 },
    { key: 'strategy', icon: Compass },
  ];

  return (
    <section id="what-we-do" className="py-32 lg:py-40 bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-5">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 border border-white/20 rounded-full" />
        <div className="absolute top-1/3 right-1/3 w-64 h-64 border border-white/10 rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-20 lg:mb-24">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
            <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">Services</span>
          </div>
          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-extralight text-white mb-8 leading-tight">
            {t.whatWeDo.title}
          </h2>
          <p className="text-lg lg:text-xl text-zinc-500 font-light leading-relaxed">
            {t.whatWeDo.subtitle}
          </p>
        </div>

        {/* Services Grid - Bento style */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-1">
          {services.map(({ key, icon: Icon }, index) => (
            <div 
              key={key}
              className={`group relative p-8 lg:p-10 bg-zinc-950 hover:bg-zinc-900/80 border border-zinc-900 hover:border-zinc-800 transition-all duration-500 ${index === 0 ? 'lg:col-span-2' : ''}`}
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
                <div className="absolute top-0 right-0 w-px h-8 bg-gradient-to-b from-cyan-400/50 to-transparent transform origin-top-right group-hover:scale-y-150 transition-transform duration-500" />
                <div className="absolute top-0 right-0 w-8 h-px bg-gradient-to-l from-cyan-400/50 to-transparent transform origin-top-right group-hover:scale-x-150 transition-transform duration-500" />
              </div>

              <div className="relative z-10">
                <div className="w-14 h-14 mb-8 flex items-center justify-center border border-zinc-800 group-hover:border-cyan-500/30 transition-colors duration-500">
                  <Icon className="w-6 h-6 text-zinc-500 group-hover:text-cyan-400 transition-colors duration-500" />
                </div>
                
                <h3 className="text-xl lg:text-2xl font-light text-white mb-4 group-hover:text-cyan-50 transition-colors duration-300">
                  {t.whatWeDo.services[key].title}
                </h3>
                <p className="text-sm lg:text-base text-zinc-500 leading-relaxed group-hover:text-zinc-400 transition-colors duration-300">
                  {t.whatWeDo.services[key].description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
